import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {v4 as uuidv4} from 'uuid';

import {FONT_PROXIMA_9, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHT, NEAR_BLACK, WHITE} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

const MAX_COLOR_BANDS = 4;

/**
 * Renders a text label at the given translation.
 * @param label The label to display
 * @param cx The x-translation of the center of the label
 * @param cy The y-translation of the center of the label
 */
function Label({label, cx, cy, fill}: {label: string; cx: number; cy: number; fill: string}): JSX.Element {
  return (
    <g transform={`translate(${cx},${cy})`}>
      <text
        className={css(styles.text)}
        fill={fill}
        dominantBaseline="central"
        textAnchor="middle"
        cx={0}
        cy={0}
      >
        {label}
      </text>
    </g>
  );
}

interface MulticolorStripedCircleProps {
  /** The x-translation of the well relative to the plate map. */
  cx: number;
  /** The y-translation of the well relative to the plate map. */
  cy: number;
  /** The radius of the well. */
  radius: number;
  /** The list of colors to render in the well. */
  colors: Array<string>;
  /** What icon to display next to the stripe count if there are too many stripes to display them all. Default is 'flask'. */
  iconOverride?: BenchlingIconName;
  /** A label to display in the center of the well, no matter how many colors are passed */
  labelOverride?: string;
  /** Opacity of the background color(s) of the well */
  fillOpacity?: number;
}

export default function MulticolorStripedCircle({
  cx,
  cy,
  radius,
  colors,
  iconOverride,
  labelOverride,
  fillOpacity,
}: MulticolorStripedCircleProps): JSX.Element {
  const size = radius * 2;
  const colorBandHeight = size / colors.length;
  const topLeftX = cx - radius;
  const topLeftY = cy - radius;

  // Color band rects will reference this id attr for each well; needs to be unique per-well across entire DOM
  const uuid = uuidv4();
  return (
    <svg
      width={size}
      height={size}
      className={css(styles.svg)}
      {...getTestProps({component: 'MulticolorStripedCircle', element: 'root'})}
    >
      {colors.length <= MAX_COLOR_BANDS && (
        <g>
          <defs>
            {/* Clip everything outside the well... */}
            <clipPath
              id={`clipping-circle-${uuid}`}
              {...getTestProps({component: 'MulticolorStripedCircle', element: 'clippingCircle'})}
            >
              <circle cx={cx} cy={cy} r={radius} />
            </clipPath>
          </defs>
          {/* ...then just draw a bunch of horizontal bands instead of trying to do arcs */}
          {colors.map((color, index) => (
            <rect
              x={topLeftX}
              y={topLeftY + index * colorBandHeight}
              width={size}
              height={colorBandHeight}
              fill={color}
              key={index}
              clipPath={`url(#clipping-circle-${uuid})`}
              fillOpacity={fillOpacity ?? 1}
              {...getTestProps({component: 'MulticolorStripedCircle', element: 'stripe', key: `${index}`})}
            />
          ))}
          {labelOverride != null && (
            <Label
              label={labelOverride}
              cx={cx}
              cy={cy}
              fill={fillOpacity != null && fillOpacity > 0.5 ? WHITE : NEAR_BLACK}
            />
          )}
        </g>
      )}
      {/* Or, if we have too many colors for the color bands to be a useful visual aid, render a number. */}
      {colors.length > MAX_COLOR_BANDS && (
        <g>
          <circle cx={cx} cy={cy} r={radius} fill={GRAY_LIGHT} stroke={NEAR_BLACK} strokeWidth={1} />
          {labelOverride == null ? (
            <foreignObject height={size} width={size} x={topLeftX} y={topLeftY}>
              <div className={css(styles.stripeCountFlexWrapper)}>
                <BenchlingIcon icon={iconOverride ?? 'flask'} className={css(styles.stripeCountIcon)} />
                <span
                  className={css(styles.stripeCountText)}
                  {...getTestProps({component: 'MulticolorStripedCircle', element: 'stripeCount'})}
                >
                  {colors.length}
                </span>
              </div>
            </foreignObject>
          ) : (
            <Label label={labelOverride} cx={cx} cy={cy} fill={WHITE} />
          )}
        </g>
      )}
    </svg>
  );
}

const styles = StyleSheet.create({
  svg: {overflow: 'visible'},
  text: {cursor: 'inherit', fontSize: '0.8em'},
  stripeCountFlexWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    color: NEAR_BLACK,
  },
  stripeCountIcon: {
    height: '9px',
    marginRight: '2px',
  },
  stripeCountText: {
    ...FONT_PROXIMA_9,
    // This is necessary for the count text to appear vertically aligned, especially when the well is selected
    lineHeight: 1,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
});
